import { EOIcon } from '@nx/ui/eo';
import { useI18n } from '@nx/i18n';
import {
  computeChecklistProgress,
  nextChecklistStatus,
  taskDueAlertLevel,
  type ChecklistPhase,
  type ChecklistTask,
} from '@nx/domain';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  checklistTaskCreateRequested,
  checklistTaskUpdateRequested,
  checklistTemplateApplyRequested,
  checklistWatchRequested,
} from '../../store/slices/checklistSlice';
import { applyChecklistTemplate } from '../../services/eoCallables';
import { useEoToast } from '../../hooks/useEoToast';

const PHASES: { id: ChecklistPhase; labelKey: string }[] = [
  { id: 'pre', labelKey: 'eo.checklist.pre' },
  { id: 'during', labelKey: 'eo.checklist.during' },
  { id: 'post', labelKey: 'eo.checklist.post' },
];

export function ChecklistPage() {
  const { t } = useI18n();
  const { eventId = '' } = useParams();
  const dispatch = useAppDispatch();
  const { toast } = useEoToast();
  const tasks = useAppSelector((s) => s.checklist.items);

  useEffect(() => {
    dispatch(checklistWatchRequested(eventId));
  }, [dispatch, eventId]);

  async function applyTemplate() {
    try {
      const res = await applyChecklistTemplate(eventId, 'corporate-retreat');
      toast(`${res.count} tasks`, 'success');
    } catch {
      dispatch(checklistTemplateApplyRequested({ eventId, templateId: 'corporate-retreat' }));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h2>{t('eo.nav.checklist')}</h2>
          <p>{t('eo.checklist.subtitle')}</p>
        </div>
        <div className="page-head-actions">
          <button type="button" className="btn btn-outline" onClick={() => void applyTemplate()}>
            {t('eo.checklist.useTemplate')}
          </button>
        </div>
      </div>

      <div className="kanban">
        {PHASES.map((phase) => (
          <KanbanColumn
            key={phase.id}
            phase={phase.id}
            title={t(phase.labelKey as never)}
            tasks={tasks.filter((t) => t.phase === phase.id)}
            allTasks={tasks}
            onAdd={(title) =>
              dispatch(
                checklistTaskCreateRequested({
                  eventId,
                  input: {
                    phase: phase.id,
                    title,
                    sortOrder: tasks.filter((x) => x.phase === phase.id).length,
                  },
                }),
              )
            }
            onStatusClick={(task) =>
              dispatch(
                checklistTaskUpdateRequested({
                  eventId,
                  taskId: task.id,
                  input: { status: nextChecklistStatus(task.status) },
                }),
              )
            }
          />
        ))}
      </div>
    </>
  );
}

function KanbanColumn({
  phase,
  title,
  tasks,
  allTasks,
  onAdd,
  onStatusClick,
}: {
  phase: ChecklistPhase;
  title: string;
  tasks: ChecklistTask[];
  allTasks: ChecklistTask[];
  onAdd: (title: string) => void;
  onStatusClick: (task: ChecklistTask) => void;
}) {
  const { t } = useI18n();
  const progress = computeChecklistProgress(allTasks, phase);
  const [newTitle, setNewTitle] = useState('');

  return (
    <div className="kanban-col">
      <h3>{title}</h3>
      <div className="progress">
        <span>
          {progress.done}/{progress.total}
        </span>
        <div className="progress-bar">
          <div style={{ width: `${progress.percent}%` }} />
        </div>
      </div>
      {tasks.map((task) => {
        const dueAlert = taskDueAlertLevel(task.dueDate, task.status);
        return (
          <div
            key={task.id}
            className={`task-card ${dueAlert === 'urgent' || dueAlert === 'overdue' ? 'urgent' : ''} ${task.status === 'done' ? 'done' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onStatusClick(task)}
            onKeyDown={(e) => e.key === 'Enter' && onStatusClick(task)}
          >
            <strong>{task.title}</strong>
            <div className="row">
              <span className={`badge ${task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'warn' : 'sage'}`}>
                {task.priority}
              </span>
              <span>{task.dueDate?.toISOString().slice(0, 10)}</span>
            </div>
            <div className="tags">
              {task.tags.map((tag) => (
                <span key={tag} className="badge muted">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <input
          className="input"
          placeholder={t('eo.checklist.newTask')}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => {
            if (newTitle.trim()) {
              onAdd(newTitle.trim());
              setNewTitle('');
            }
          }}
        >
          <EOIcon n="plus" size={14} />
        </button>
      </div>
    </div>
  );
}
